import { useEffect, useState } from 'react';
import CurrencyApi from '../../../api/CurrencyApi';
import { getMultiCurrencySettings } from '../../../utils/multiCurrencySettings';
import { buildExchangeRateLookupFromCurrencyList } from '../../../utils/reportCurrencyAggregate';
import { logReportCurrency } from '../debug/reportCurrencyDebug';

/**
 * Loads org base currency + exchange rates for report/matrix/chart conversion
 * (same as ReportPreview / ReportDataViewer).
 */
export function useReportCurrency() {
  const [baseCurrencyCode, setBaseCurrencyCode] = useState(null);
  const [exchangeRateLookup, setExchangeRateLookup] = useState(null);
  const [multiCurrencyEnabled, setMultiCurrencyEnabled] = useState(true);
  const [currencyReady, setCurrencyReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let loadedBase = null;
      let loadedLookup = null;
      let loadedMulti = true;

      try {
        const currencyApi = new CurrencyApi();
        const res = await currencyApi.fetchAll();
        const list = res?.data?.data || res?.data || [];
        const arr = Array.isArray(list) ? list : [];
        const baseRow = arr.find((r) => r.is_base_currency === true);
        loadedBase = baseRow?.currency_code
          ? String(baseRow.currency_code).trim()
          : null;
        loadedLookup = buildExchangeRateLookupFromCurrencyList(arr);
        if (!cancelled) {
          setBaseCurrencyCode(loadedBase);
          setExchangeRateLookup(loadedLookup);
        }
      } catch (e) {
        console.error('useReportCurrency: failed to load base currency', e);
        if (!cancelled) setExchangeRateLookup(null);
      }

      try {
        const s = await getMultiCurrencySettings();
        loadedMulti = !!s.multiCurrencyEnabled;
        if (!cancelled) setMultiCurrencyEnabled(loadedMulti);
      } catch {
        loadedMulti = true;
        if (!cancelled) setMultiCurrencyEnabled(true);
      }

      if (!cancelled) {
        setCurrencyReady(true);
        logReportCurrency('useReportCurrency.loaded', {
          baseCurrencyCode: loadedBase,
          multiCurrencyEnabled: loadedMulti,
          lookupKeyCount: loadedLookup ? Object.keys(loadedLookup).length : 0,
          lookupSample: loadedLookup
            ? Object.keys(loadedLookup).slice(0, 6)
            : [],
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const reportConversionBaseCode = multiCurrencyEnabled ? baseCurrencyCode : null;
  const reportConversionLookup = multiCurrencyEnabled ? exchangeRateLookup : null;

  useEffect(() => {
    if (!currencyReady) return;
    logReportCurrency('useReportCurrency.ready', {
      baseCurrencyCode,
      multiCurrencyEnabled,
      reportConversionBaseCode,
      hasLookup: Boolean(reportConversionLookup),
      lookupKeyCount: reportConversionLookup
        ? Object.keys(reportConversionLookup).length
        : 0,
    });
  }, [
    currencyReady,
    baseCurrencyCode,
    multiCurrencyEnabled,
    reportConversionBaseCode,
    reportConversionLookup,
  ]);

  return {
    baseCurrencyCode,
    exchangeRateLookup,
    multiCurrencyEnabled,
    reportConversionBaseCode,
    reportConversionLookup,
    currencyReady,
  };
}

export default useReportCurrency;
